import * as React from 'react';
import { Component, Fragment } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { utils } from '@kurtosys/ksys-app-template';
import { ChangeIcon } from '../models/configuration/components/ChangeIcon';
import { DisclaimerElement } from '../models/configuration/DisclaimerElement';
import Translate from './Translate';
import FundFactValue from './FundFactValue';
import {
    AccessibilityStore,
    ConditionalDisclaimerStore,
    DataStore,
    EntityStore,
    SuperscriptScope,
} from '../stores';
import InlineDisclaimers from './InlineDisclaimers';
import { Theme } from '../models/Theme';

const FundFactListItem = styled<any, any>(
    'li',
    'fundFactListItem',
    'fundFactItem'
)`
    ${propStyles};
`;

FundFactListItem.defaultProps = {
    flex: ['0 1 50%', '0 1 50%', '0 1 25%'],
    display: 'flex',
    minHeight: '80px',
};

const FundFact = styled<any, any>('div', 'fundFact', 'fundFactItem')`
    ${propStyles};
`;

FundFact.defaultProps = {
    flex: '1 1 100%', // Bugfix IE11 - set specific flex-basis to make long text wrap
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'md',
    margin: 'xs',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray500',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const FundFactHeading = styled<any, any>(
    'div',
    'fundFactHeading',
    'fundFactItem'
)`
    ${propStyles};
`;

FundFactHeading.defaultProps = {
    color: 'body',
    fontFamily: 'heading',
    fontWeight: 'bold',
    fontSize: 'md',
    textAlign: 'center',
    margin: '0 0 0.5em 0',
    width: '100%', // Bugfix IE11 - set specific width to make long text wrap
};

const FundFactAsAtDate = styled<any, any>(
    'div',
    'fundFactAsAtDate',
    'fundFactItem'
)`
    ${propStyles};
`;

FundFactAsAtDate.defaultProps = {
    color: 'gray500',
    fontFamily: 'body',
    fontWeight: 'regular',
    fontSize: 'xs',
    margin: '0.5em 0 0 0',
};

const FundFactValueWrapper = styled<any, any>(
    'div',
    'fundFactValueWrapper',
    'fundFactItem'
)`
    ${propStyles};
`;

FundFactValueWrapper.defaultProps = {
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'primary',
    fontFamily: 'body',
    fontWeight: 'regular',
    fontSize: 'lg',
    textAlign: 'center',
};

const FundFactLabel = styled<any, any>(
    'label',
    'fundFactLabel',
    'fundFactItem'
)`
    ${propStyles}
`;

const FundFactValueLabel = styled<any, any>(
    'span',
    'fundFactValueLabel',
    'fundFactItem'
)`
    ${propStyles}
`;

const FundFactLabelValue = styled<any, any>(
    'div',
    'fundFactLabelValue',
    'fundFactItem'
)`
    ${propStyles}
`;

interface FundFactItemProps {
    /** Injected */
    dataStore?: DataStore;
    entityStore?: EntityStore;
    accessibilityStore?: AccessibilityStore;
    /** End Injected */
    theme?: Theme;
    card: FundFactCard;
    multipleValueLayout?: 'row' | 'column';
}

interface FundFactCard {
    label: string;
    asAtDate: FundFactCardAsAtDate;
    data: FundFactCardData | Array<FundFactCardData> | any; // TODO: Figure out type error for data.map, data.value without any
    multiple: boolean;
    disclaimerConfig: DisclaimerElement;
    disclaimers: FundFactDisclaimers;
    ui: 'srri' | 'stars' | 'starsOutOf';
    higherRiskLabel: string;
    lowerRiskLabel: string;
    showChange: boolean;
    changeIcon: ChangeIcon;
    noDataPlaceholder?: string;
}

interface FundFactCardData {
    value: string | number | boolean;
}

interface FundFactCardAsAtDate {
    label: string;
    value: string;
}

interface FundFactDisclaimers {
    character: string;
    content: string;
    footnote: boolean;
}

@inject('accessibilityStore', 'dataStore', 'entityStore')
@observer
class FundFactItem extends Component<FundFactItemProps> {
    constructor(props) {
        super(props);
        FundFactValueWrapper.defaultProps.flexDirection =
            props.multipleValueLayout;
    }
    // For multiple data points return false if any of them are false
    // Otherwise return the single value
    @computed
    get value() {
        if (this.props.card.multiple) {
            return !this.props.card.data.some(d => d.value === false);
        }

        return this.props.card.data.value;
    }

    @computed
    get cardLabelKey() {
        const { card } = this.props;
        return utils.changeCase.camelCase(card.label);
    }

    @computed
    get fundFactValueId() {
        return `fundFact-value-${this.cardLabelKey}`;
    }

    @computed
    get ariaLabel() {
        const { card, accessibilityStore } = this.props;
        const { label, ui } = card;
        const factAccessibilityGroupText = accessibilityStore.factAccessibilityValue(
            ui
        );

        return `${label} ${factAccessibilityGroupText}`;
    }

    @computed
    get conditionalDisclaimers() {
        const { card } = this.props;
        if (card.disclaimerConfig) {
            return [
                ConditionalDisclaimerStore.convertRowConfigDisclaimer(
                    card.disclaimerConfig
                ),
            ];
        }
        return [];
    }

    render() {
        const { theme, card, dataStore, entityStore } = this.props;
        const { value } = this;
        if (value !== false) {
            return (
                <FundFactListItem
                    {...theme.FundFactItem}
                    aria-label={this.ariaLabel}
                >
                    <FundFact {...theme.FundFact}>
                        <FundFactHeading {...theme.FundFactHeading}>
                            <FundFactLabel
                                defaultStyleProps={{
                                    color: 'body',
                                    fontFamily: 'heading',
                                    fontWeight: 'bold',
                                    fontSize: 'md',
                                    display: 'inline-block',
                                    margin: '0',
                                }}
                                htmlFor={this.fundFactValueId}
                            >
                                <Translate>{card.label}</Translate>
                            </FundFactLabel>
                            {entityStore?.value &&
                                this.conditionalDisclaimers.length > 0 && (
                                    <InlineDisclaimers
                                        conditionalDisclaimers={
                                            this.conditionalDisclaimers
                                        }
                                        superscriptContext={
                                            entityStore.value.clientCode
                                        }
                                        superscriptScope={
                                            SuperscriptScope.fundCard
                                        }
                                        accessibilityContext={{
                                            key: `More information about the {label}`,
                                            placeholders: {
                                                label: card.label
                                            }
                                        }}
                                        metricContext={`fundFactItem-heading-${this.cardLabelKey}`}
                                        forceSuperScripts={dataStore.isMobile}
                                        analyticsContext={{
                                            source: 'fundFactItem',
                                            sourceContext: card.label,
                                        }}
                                    />
                                )}
                            {card.asAtDate.value && (
                                <FundFactAsAtDate {...theme.FundFactAsAtDate}>
                                    <Translate>{card.asAtDate.label}</Translate>{' '}
                                    {card.asAtDate.value}
                                </FundFactAsAtDate>
                            )}
                        </FundFactHeading>
                        <FundFactValueWrapper
                            id={this.fundFactValueId}
                            {...theme.FundFactValue}
                        >
                            {card.multiple ? (
                                <Fragment>
                                    {card.data.map((data, i) => {
                                        return (
                                            <FundFactLabelValue
                                                key={i}
                                                {...theme.FundFactLabelValueWrapper}
                                            >
                                                {data.label && (
                                                    <FundFactValueLabel
                                                        {...theme.FundFactValueLabel}
                                                    >
                                                        {data.label}
                                                    </FundFactValueLabel>
                                                )}
                                                <FundFactValue
                                                    dataStore={dataStore}
                                                    ui={card.ui}
                                                    value={data.value}
                                                    separator={data.separator}
                                                    showChange={data.showChange}
                                                    higherRiskLabel={
                                                        card.higherRiskLabel
                                                    }
                                                    lowerRiskLabel={
                                                        card.lowerRiskLabel
                                                    }
                                                    alignIcon={
                                                        (data.changeIcon &&
                                                            data.changeIcon
                                                                .align) ||
                                                        (card.changeIcon &&
                                                            card.changeIcon
                                                                .align) ||
                                                        'right'
                                                    }
                                                    upIcon={
                                                        (data.changeIcon &&
                                                            data.changeIcon
                                                                .up) ||
                                                        (card.changeIcon &&
                                                            card.changeIcon.up)
                                                    }
                                                    downIcon={
                                                        (data.changeIcon &&
                                                            data.changeIcon
                                                                .down) ||
                                                        (card.changeIcon &&
                                                            card.changeIcon
                                                                .down)
                                                    }
                                                    noDataPlaceholder={
                                                        card.noDataPlaceholder
                                                    }
                                                />
                                            </FundFactLabelValue>
                                        );
                                    })}
                                </Fragment>
                            ) : (
                                <FundFactLabelValue>
                                    {card.data.label && (
                                        <FundFactValueLabel>
                                            {card.data.label}
                                        </FundFactValueLabel>
                                    )}
                                    <FundFactValue
                                        dataStore={dataStore}
                                        ui={card.ui}
                                        value={card.data.value}
                                        showChange={card.showChange}
                                        higherRiskLabel={card.higherRiskLabel}
                                        lowerRiskLabel={card.lowerRiskLabel}
                                        alignIcon={
                                            (card.changeIcon &&
                                                card.changeIcon.align) ||
                                            'right'
                                        }
                                        upIcon={
                                            card.changeIcon &&
                                            card.changeIcon.up
                                        }
                                        downIcon={
                                            card.changeIcon &&
                                            card.changeIcon.down
                                        }
                                        noDataPlaceholder={
                                            card.noDataPlaceholder
                                        }
                                    />
                                </FundFactLabelValue>
                            )}
                        </FundFactValueWrapper>
                    </FundFact>
                </FundFactListItem>
            );
        }

        return null;
    }
}

export default withTheme(FundFactItem);



// WEBPACK FOOTER //
// ./src/Components/FundFactItem.tsx